import React, { useState, useMemo } from 'react'
import { CoreTeam, CoreTeamTableMetaProps, Allocation, CoreTeam as CoreTeamType } from '../data/types'
import { Button, Modal } from './ui'
import { CheckIcon, XIcon, EditIcon, DeleteIcon, PauseIcon, PlayIcon, DownloadIcon } from '../icons'
import { useAuth } from '../routes/auth/AuthContext'
import { formatDateOnly } from '../utils/dates'

type Props = {
    meta: CoreTeamTableMetaProps
    data: CoreTeam[]
    onApprove: (id: number) => void
    onReject: (id: number) => void
    onUnapprove: (id: number) => void
    onEdit: (member: CoreTeam) => void
    onDelete: (id: number) => void
    onToggleStatus: (id: number, newStatus: 'active' | 'on hold') => void
    onBulkApprove?: (ids: number[]) => Promise<void>
    onBulkReject?: (ids: number[]) => Promise<void>
    onBulkUnapprove?: (ids: number[]) => Promise<void>
    onBulkDelete?: (ids: number[]) => Promise<void>
    onBulkPutOnHold?: (ids: number[]) => Promise<void>
    onBulkMakeActive?: (ids: number[]) => Promise<void>
    userHasApprovePermission: boolean
    userHasManagePermission: boolean
    streamFilter: string
    tierFilter: number | ''
    statusFilter: string
    activityStatusFilter: string
    duplicationsFilter: boolean | null
    onStreamFilterChange: (value: string) => void
    onTierFilterChange: (value: number | '') => void
    onStatusFilterChange: (value: string) => void
    onActivityStatusFilterChange: (value: string) => void
    onDuplicationsFilterChange: () => void
}

export const CoreTeamTable = (props: Props) => {
    const { user } = useAuth()
    const {
        meta, data, onApprove, onReject, onUnapprove, onEdit, onDelete, onToggleStatus,
        onBulkApprove, onBulkReject, onBulkUnapprove, onBulkDelete, onBulkPutOnHold, onBulkMakeActive,
        userHasApprovePermission, userHasManagePermission,
        streamFilter, tierFilter, statusFilter, activityStatusFilter, duplicationsFilter,
        onStreamFilterChange, onTierFilterChange, onStatusFilterChange, onActivityStatusFilterChange, onDuplicationsFilterChange
    } = props
    const [sortColumn, setSortColumn] = useState<string>('volunteer.name')
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
    const [searchTerm, setSearchTerm] = useState('')
    const [showAllocationsModal, setShowAllocationsModal] = useState(false)
    const [selectedVolunteer, setSelectedVolunteer] = useState<CoreTeam | null>(null)
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [activeAction, setActiveAction] = useState<'approve' | 'reject' | 'unapprove' | 'delete' | 'putOnHold' | 'makeActive' | null>(null)
    const [selectAllPages, setSelectAllPages] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortColumn(column)
            setSortDirection('asc')
        }
    }

    const sortedAndFilteredData = useMemo(() => {
        let filtered = data

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase()
            filtered = filtered.filter(item => {
                const volunteerId = item.volunteer?.csid?.toString() || ''
                const fullName = `${item.volunteer?.first_name} ${item.volunteer?.last_name}`.toLowerCase()
                const role = item.role?.toLowerCase() || ''
                const streams = item.streams?.join(' ').toLowerCase() || ''
                const email = item.volunteer?.email?.toLowerCase() || ''
                const phone = item.volunteer?.phone?.toLowerCase() || ''

                return volunteerId.includes(term) ||
                       fullName.includes(term) ||
                       role.includes(term) ||
                       streams.includes(term) ||
                       email.includes(term) ||
                       phone.includes(term)
            })
        }

        // Apply stream filter
        if (streamFilter) {
            filtered = filtered.filter(item => item.streams?.includes(streamFilter as any))
        }

        // Apply tier filter
        if (tierFilter !== '') {
            filtered = filtered.filter(item => item.tier === tierFilter)
        }

        // Apply status filter
        if (statusFilter === 'approved') {
            filtered = filtered.filter(item => item.approved === true)
        } else if (statusFilter === 'pending') {
            filtered = filtered.filter(item => item.approved === false)
        }

        // Apply activity status filter
        if (activityStatusFilter === 'active') {
            // Show approved records with active status
            filtered = filtered.filter(item => item.status === 'active')
        } else if (activityStatusFilter === 'on hold') {
            // Show all records (approved or pending) with on hold status
            filtered = filtered.filter(item => item.status === 'on hold')
        }

        // Apply duplications filter
        if (duplicationsFilter !== null) {
            filtered = filtered.filter(item => {
                // Use duplicate_check_allocations_count which excludes allocations from events with include_in_duplicate_checks = false
                const allocationsCount = item.volunteer?.duplicate_check_allocations_count ?? 0
                const coreTeamsCount = item.volunteer?.core_teams_count ?? 0
                // Use filtered lead counts that exclude positions from events with include_in_duplicate_checks = false
                const leadPositionsCount = (
                    (item.volunteer?.duplicate_check_event_leads_count ?? 0) +
                    (item.volunteer?.duplicate_check_areas_as_lead_count ?? 0) +
                    (item.volunteer?.duplicate_check_areas_as_deputy_lead_count ?? 0) +
                    (item.volunteer?.duplicate_check_shifts_as_lead_count ?? 0) +
                    (item.volunteer?.duplicate_check_shifts_as_deputy_lead_count ?? 0)
                )
                const totalCount = allocationsCount + coreTeamsCount + leadPositionsCount
                const hasDuplications = totalCount > 1

                return duplicationsFilter ? hasDuplications : !hasDuplications
            })
        }

        // Apply sorting
        return [...filtered].sort((a, b) => {
            let aVal: any, bVal: any

            switch (sortColumn) {
                case 'volunteer.csid':
                    aVal = a.volunteer?.csid || 0
                    bVal = b.volunteer?.csid || 0
                    break
                case 'volunteer.name':
                    aVal = `${a.volunteer?.first_name || ''} ${a.volunteer?.last_name || ''}`.toLowerCase()
                    bVal = `${b.volunteer?.first_name || ''} ${b.volunteer?.last_name || ''}`.toLowerCase()
                    break
                case 'streams':
                    aVal = a.streams?.join(', ') || ''
                    bVal = b.streams?.join(', ') || ''
                    break
                case 'role':
                    aVal = a.role || ''
                    bVal = b.role || ''
                    break
                case 'tier':
                    aVal = a.tier ?? 99
                    bVal = b.tier ?? 99
                    break
                case 'approved':
                    aVal = a.approved ? 1 : 0
                    bVal = b.approved ? 1 : 0
                    break
                default:
                    return 0
            }

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
            return 0
        })
    }, [data, sortColumn, sortDirection, searchTerm, streamFilter, tierFilter, statusFilter, activityStatusFilter, duplicationsFilter])

    // Pagination calculations
    const totalItems = sortedAndFilteredData.length
    const totalPages = Math.ceil(totalItems / rowsPerPage)
    const startIndex = (currentPage - 1) * rowsPerPage
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems)
    const paginatedData = sortedAndFilteredData.slice(startIndex, endIndex)

    // Reset to page 1 and clear selectAllPages when filters change
    React.useEffect(() => {
        setCurrentPage(1)
        setSelectAllPages(false)
        setSelectedIds(new Set())
    }, [searchTerm, streamFilter, tierFilter, statusFilter, activityStatusFilter, duplicationsFilter])

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const SortIcon = ({ column }: { column: string }) => {
        if (sortColumn !== column) {
            return <span className="text-gray-400">⇅</span>
        }
        return <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
    }

    const allStreams: string[] = ['Commercial', 'Comms', 'External Venue', 'Finance', 'HR', 'Intezaar', 'Ismaili Centre', 'Jamati Experience', 'Medical', 'NCF', 'Registration', 'Safety & Security']
    const allTiers = [0, 1, 2, 3, 4, 5]

    const handleDuplicationsClick = () => {
        onDuplicationsFilterChange()
    }

    const DuplicationsIcon = () => {
        if (duplicationsFilter === null) {
            return <span className="text-gray-400">○</span>
        }
        return duplicationsFilter ? (
            <span className="text-success-500">✓</span>
        ) : (
            <span className="text-danger-500">✗</span>
        )
    }

    // Selection handlers
    const handleSelectAll = () => {
        if (isAllSelectedOnPage && !selectAllPages) {
            // Already selected all on page, deselect all
            setSelectedIds(new Set())
            setSelectAllPages(false)
        } else {
            // Select all on current page
            const newSelected = new Set(selectedIds)
            paginatedData.forEach(item => newSelected.add(item.id))
            setSelectedIds(newSelected)
            setSelectAllPages(false)
        }
    }

    const handleSelectAllPages = () => {
        setSelectedIds(new Set(sortedAndFilteredData.map(item => item.id)))
        setSelectAllPages(true)
    }

    const handleClearSelection = () => {
        setSelectedIds(new Set())
        setSelectAllPages(false)
    }

    const handleSelectOne = (id: number) => {
        const newSelected = new Set(selectedIds)
        if (newSelected.has(id)) {
            newSelected.delete(id)
        } else {
            newSelected.add(id)
        }
        setSelectedIds(newSelected)
        setSelectAllPages(false) // Clear "select all pages" when manually selecting/deselecting
    }

    const isAllSelectedOnPage = paginatedData.length > 0 && paginatedData.every(item => selectedIds.has(item.id))
    const isSomeSelectedOnPage = paginatedData.some(item => selectedIds.has(item.id)) && !isAllSelectedOnPage
    const showSelectAllPagesOption = isAllSelectedOnPage && !selectAllPages && sortedAndFilteredData.length > paginatedData.length

    // Bulk action handlers
    const handleBulkAction = async (action: 'approve' | 'reject' | 'unapprove' | 'delete' | 'putOnHold' | 'makeActive') => {
        if (selectedIds.size === 0) return

        const idsArray = Array.from(selectedIds)
        setActiveAction(action)

        try {
            switch (action) {
                case 'approve':
                    if (onBulkApprove) await onBulkApprove(idsArray)
                    break
                case 'reject':
                    if (onBulkReject) await onBulkReject(idsArray)
                    break
                case 'unapprove':
                    if (onBulkUnapprove) await onBulkUnapprove(idsArray)
            setSelectAllPages(false)
                    break
                case 'delete':
                    if (onBulkDelete) await onBulkDelete(idsArray)
                    break
                case 'putOnHold':
                    if (onBulkPutOnHold) await onBulkPutOnHold(idsArray)
                    break
                case 'makeActive':
                    if (onBulkMakeActive) await onBulkMakeActive(idsArray)
                    break
            }
            setSelectedIds(new Set())
        } finally {
            setActiveAction(null)
        }
    }

    // CSV Export function
    const handleExportCSV = () => {
        // Use filtered data for export
        const dataToExport = sortedAndFilteredData

        // Define CSV headers
        const headers = [
            'ID',
            'First Name',
            'Last Name',
            'Email',
            'Phone',
            'Age',
            'Age Band',
            'Ismaili',
            'Inter-Faith Family',
            'Streams',
            'Role',
            'Tier',
            'Notes',
            'Approval Status',
            'Activity Status',
            'Submitted By'
        ]

        // Convert data to CSV rows
        const rows = dataToExport.map(item => [
            item.volunteer?.csid ? `="${item.volunteer.csid}"` : '',
            item.volunteer?.first_name || '',
            item.volunteer?.last_name || '',
            item.volunteer?.email || '',
            item.volunteer?.phone ? `="${item.volunteer.phone}"` : '',
            item.volunteer?.age || '',
            item.volunteer?.age_band || '',
            item.volunteer?.ismaili ? 'Yes' : 'No',
            item.volunteer?.multi_faith_family ? 'Yes' : 'No',
            item.streams?.join('; ') || '',
            item.role || '',
            item.tier?.toString() || '',
            item.notes || '',
            item.approved ? 'Approved' : 'Pending',
            item.status || '',
            item.addedBy ? `${item.addedBy.first_name} ${item.addedBy.last_name}` : ''
        ])

        // Combine headers and rows
        const csvRows = [headers]
        rows.forEach(row => {
            const processedRow = row.map(cell => {
                let cellStr = String(cell)
                // Replace newlines and carriage returns with spaces
                cellStr = cellStr.replace(/[\r\n]+/g, ' ').trim()
                // Don't escape Excel formulas (cells starting with =)
                if (cellStr.startsWith('=')) {
                    return cellStr
                }
                // Escape cells containing commas or quotes
                if (cellStr.includes(',') || cellStr.includes('"')) {
                    return `"${cellStr.replace(/"/g, '""')}"`
                }
                return cellStr
            })
            csvRows.push(processedRow)
        })

        const csvContent = csvRows.map(row => row.join(',')).join('\n')

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        // Generate filename with current date
        const date = new Date().toISOString().split('T')[0]
        link.setAttribute('href', url)
        link.setAttribute('download', `core_team_export_${date}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            {/* Search and Filters */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                {/* Search Bar */}
                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Search by ID, name, role, stream, email, or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    {/* Stream Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.streamFilterLabel || 'Stream'}
                        </label>
                        <select
                            value={streamFilter}
                            onChange={(e) => onStreamFilterChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">{meta.allStreamsLabel || 'All Streams'}</option>
                            {allStreams.map(stream => (
                                <option key={stream} value={stream}>{stream}</option>
                            ))}
                        </select>
                    </div>

                    {/* Tier Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.tierFilterLabel || 'Tier'}
                        </label>
                        <select
                            value={tierFilter}
                            onChange={(e) => onTierFilterChange(e.target.value === '' ? '' : Number(e.target.value))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">{meta.allTiersLabel || 'All Tiers'}</option>
                            {allTiers.map(tier => (
                                <option key={tier} value={tier}>{tier === 5 ? 'R' : tier}</option>
                            ))}
                        </select>
                    </div>

                    {/* Approval Status Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.statusFilterLabel || 'Status'}
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => onStatusFilterChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">{meta.allStatusLabel || 'All Status'}</option>
                            <option value="approved">{meta.approvedStatus}</option>
                            <option value="pending">{meta.pendingStatus}</option>
                        </select>
                    </div>

                    {/* Activity Status Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.activityStatusFilterLabel || 'Activity Status'}
                        </label>
                        <select
                            value={activityStatusFilter}
                            onChange={(e) => onActivityStatusFilterChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        >
                            <option value="">{meta.allActivityStatusLabel || 'All'}</option>
                            <option value="active">{meta.activeStatus}</option>
                            <option value="on hold">{meta.onHoldStatus}</option>
                        </select>
                    </div>

                    {/* Duplications Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.duplicationsFilterLabel || 'Duplications'}
                        </label>
                        <button
                            onClick={handleDuplicationsClick}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-start flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                            <span>
                                {duplicationsFilter === null ? meta.anyDuplicationsLabel || 'Any' :
                                 duplicationsFilter ? meta.hasDuplicationsLabel || 'Has Duplications' :
                                 meta.noDuplicationsLabel || 'No Duplications'}
                            </span>
                            <DuplicationsIcon />
                        </button>
                    </div>
                </div>

                {/* Export Button */}
                {user?.role === 'admin' && (
                    <div className="mt-4 flex justify-end">
                        <button
                            onClick={handleExportCSV}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            <DownloadIcon className="w-4 h-4" />
                            {meta.exportButton || 'Export to CSV'}
                        </button>
                    </div>
                )}
            </div>

            {/* Select All Pages Banner */}
            {showSelectAllPagesOption && (
                <div className="p-3 bg-primary-100 dark:bg-primary-800 border-b border-primary-200 dark:border-primary-700">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            All {paginatedData.length} items on this page are selected.
                        </span>
                        <button
                            onClick={handleSelectAllPages}
                            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 underline"
                        >
                            Select all {sortedAndFilteredData.length} items
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Actions Toolbar */}
            {selectedIds.size > 0 && (
                <div className="p-4 bg-primary-50 dark:bg-primary-900 border-b border-primary-200 dark:border-primary-700">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {selectAllPages
                                    ? `All ${sortedAndFilteredData.length} items selected`
                                    : (meta.selectedCountLabel ? meta.selectedCountLabel(selectedIds.size) : `${selectedIds.size} selected`)
                                }
                            </span>
                            <button
                                onClick={handleClearSelection}
                                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                                disabled={activeAction !== null}
                            >
                                {meta.clearSelectionLabel || 'Clear selection'}
                            </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {userHasApprovePermission && onBulkApprove && (
                                <button
                                    onClick={() => handleBulkAction('approve')}
                                    disabled={activeAction !== null}
                                    className="flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm bg-success-600 text-white rounded hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                >
                                    {activeAction === 'approve' ? meta.bulkApprovingLabel || 'Approving...' : meta.bulkApproveLabel || 'Approve'}
                                </button>
                            )}
                            {userHasApprovePermission && onBulkReject && (
                                <button
                                    onClick={() => handleBulkAction('reject')}
                                    disabled={activeAction !== null}
                                    className="flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm bg-danger-600 text-white rounded hover:bg-danger-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                >
                                    {activeAction === 'reject' ? meta.bulkRejectingLabel || 'Rejecting...' : meta.bulkRejectLabel || 'Reject'}
                                </button>
                            )}
                            {userHasApprovePermission && onBulkUnapprove && (
                                <button
                                    onClick={() => handleBulkAction('unapprove')}
                                    disabled={activeAction !== null}
                                    className="flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm bg-warn-600 text-white rounded hover:bg-warn-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                >
                                    {activeAction === 'unapprove' ? meta.bulkUnapprovingLabel || 'Unapproving...' : meta.bulkUnapproveLabel || 'Unapprove'}
                                </button>
                            )}
                            {userHasManagePermission && onBulkPutOnHold && (
                                <button
                                    onClick={() => handleBulkAction('putOnHold')}
                                    disabled={activeAction !== null}
                                    className="flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm bg-warn-600 text-white rounded hover:bg-warn-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                >
                                    {activeAction === 'putOnHold' ? meta.bulkPuttingOnHoldLabel || 'Putting On Hold...' : meta.bulkPutOnHoldLabel || 'Put On Hold'}
                                </button>
                            )}
                            {userHasManagePermission && onBulkMakeActive && (
                                <button
                                    onClick={() => handleBulkAction('makeActive')}
                                    disabled={activeAction !== null}
                                    className="flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm bg-success-600 text-white rounded hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                >
                                    {activeAction === 'makeActive' ? meta.bulkMakingActiveLabel || 'Making Active...' : meta.bulkMakeActiveLabel || 'Make Active'}
                                </button>
                            )}
                            {userHasManagePermission && onBulkDelete && (
                                <button
                                    onClick={() => handleBulkAction('delete')}
                                    disabled={activeAction !== null}
                                    className="flex-1 sm:flex-initial px-3 py-1.5 text-xs sm:text-sm bg-danger-600 text-white rounded hover:bg-danger-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                >
                                    {activeAction === 'delete' ? meta.bulkDeletingLabel || 'Deleting...' : meta.bulkDeleteLabel || 'Delete'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Desktop Table View (hidden on mobile) */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                            <th className="px-6 py-3 text-start">
                                <input
                                    type="checkbox"
                                    checked={isAllSelectedOnPage}
                                    ref={(el) => {
                                        if (el) el.indeterminate = isSomeSelectedOnPage
                                    }}
                                    onChange={handleSelectAll}
                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                />
                            </th>
                            <th
                                onClick={() => handleSort('volunteer.name')}
                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                                {meta.nameColumn} <SortIcon column="volunteer.name" />
                            </th>
                            <th
                                onClick={() => handleSort('streams')}
                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                                {meta.streamsColumn} <SortIcon column="streams" />
                            </th>
                            <th
                                onClick={() => handleSort('role')}
                                className="min-w-48 px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                                {meta.roleColumn} <SortIcon column="role" />
                            </th>
                            <th
                                onClick={() => handleSort('tier')}
                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                                {meta.tierColumn} <SortIcon column="tier" />
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.duplicationsColumn}
                            </th>
                            {(userHasApprovePermission || userHasManagePermission) && (
                                <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {meta.actionsColumn}
                                </th>
                            )}
                            <th
                                onClick={() => handleSort('approved')}
                                className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                                {meta.statusColumn} <SortIcon column="approved" />
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.activityStatusColumn}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.emailColumn}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.phoneColumn}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.submittedByColumn}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.ismailiColumn}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.familyOriginColumn}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.ageBandColumn}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.notesColumn}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {paginatedData.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(item.id)}
                                        onChange={() => handleSelectOne(item.id)}
                                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                    />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {item.volunteer?.first_name} {item.volunteer?.last_name}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    <div className="flex flex-wrap gap-1">
                                        {item.streams?.map((stream, idx) => (
                                            <span
                                                key={idx}
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                            >
                                                {stream}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                    {item.role}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-gray-100">
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 font-semibold">
                                        {item.tier === 5 ? 'R' : item.tier}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {(() => {
                                        // Use duplicate_check_allocations_count which excludes allocations from events with include_in_duplicate_checks = false
                                        const allocationsCount = item.volunteer?.duplicate_check_allocations_count ?? 0
                                        const coreTeamsCount = item.volunteer?.core_teams_count ?? 0
                                        // Use filtered lead counts that exclude positions from events with include_in_duplicate_checks = false
                                        const leadPositionsCount = (
                                            (item.volunteer?.duplicate_check_event_leads_count ?? 0) +
                                            (item.volunteer?.duplicate_check_areas_as_lead_count ?? 0) +
                                            (item.volunteer?.duplicate_check_areas_as_deputy_lead_count ?? 0) +
                                            (item.volunteer?.duplicate_check_shifts_as_lead_count ?? 0) +
                                            (item.volunteer?.duplicate_check_shifts_as_deputy_lead_count ?? 0)
                                        )
                                        const totalCount = allocationsCount + coreTeamsCount + leadPositionsCount

                                        if (totalCount === 1) {
                                            return <span className="text-gray-500 dark:text-gray-400">None</span>
                                        }

                                        return (
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedVolunteer(item)
                                                    setShowAllocationsModal(true)
                                                }}
                                            >
                                                {meta.viewAllocationsButton(totalCount)}
                                            </Button>
                                        )
                                    })()}
                                </td>
                                {(userHasApprovePermission || userHasManagePermission) && (
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <div className="flex gap-2">
                                            {userHasApprovePermission && !item.approved && (
                                                <>
                                                    <button
                                                        onClick={() => onApprove(item.id)}
                                                        className="p-2 bg-success-600 text-white rounded hover:bg-success-700 transition-colors"
                                                        title="Approve"
                                                    >
                                                        <CheckIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => onReject(item.id)}
                                                        className="p-2 bg-danger-600 text-white rounded hover:bg-danger-700 transition-colors"
                                                        title="Reject"
                                                    >
                                                        <XIcon className="w-4 h-4" />
                                                    </button>
                                                </>
                                            )}
                                            {userHasApprovePermission && item.approved && (
                                                <button
                                                    onClick={() => onUnapprove(item.id)}
                                                    className="p-2 bg-warn-600 text-white rounded hover:bg-warn-700 transition-colors"
                                                    title="Unapprove"
                                                >
                                                    <XIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                            {userHasManagePermission && (
                                                <>
                                                    <button
                                                        onClick={() => onEdit(item)}
                                                        className="p-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <EditIcon className="w-4 h-4" />
                                                    </button>
                                                    {item.status === 'active' ? (
                                                        <button
                                                            onClick={() => onToggleStatus(item.id, 'on hold')}
                                                            className="p-2 bg-warn-600 text-white rounded hover:bg-warn-700 transition-colors"
                                                            title={meta.putOnHoldButton}
                                                        >
                                                            <PauseIcon className="w-4 h-4" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => onToggleStatus(item.id, 'active')}
                                                            className="p-2 bg-success-600 text-white rounded hover:bg-success-700 transition-colors"
                                                            title={meta.makeActiveButton}
                                                        >
                                                            <PlayIcon className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => onDelete(item.id)}
                                                        className="p-2 bg-danger-600 text-white rounded hover:bg-danger-700 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <DeleteIcon className="w-4 h-4" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            item.approved
                                                ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                : 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'
                                        }`}
                                    >
                                        {item.approved ? meta.approvedStatus : meta.pendingStatus}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            item.status === 'active'
                                                ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                        }`}
                                    >
                                        {item.status === 'active' ? meta.activeStatus : meta.onHoldStatus}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {item.volunteer?.email || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {item.volunteer?.phone || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {item.addedBy ? `${item.addedBy.first_name} ${item.addedBy.last_name}` : 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {item.volunteer?.ismaili ? meta.yesLabel : meta.noLabel}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {item.volunteer?.family_origin || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                    {item.volunteer?.age_band || 'N/A'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">
                                    {item.notes || '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View (visible only on mobile) */}
            <div className="md:hidden">
                {/* Select all checkbox for mobile */}
                {paginatedData.length > 0 && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                        <input
                            type="checkbox"
                            checked={isAllSelectedOnPage}
                            ref={(el) => {
                                if (el) el.indeterminate = isSomeSelectedOnPage
                            }}
                            onChange={handleSelectAll}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Select all on this page
                        </label>
                    </div>
                )}

                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedData.map((item) => {
                    // Use duplicate_check_allocations_count which excludes allocations from events with include_in_duplicate_checks = false
                    const allocationsCount = item.volunteer?.duplicate_check_allocations_count ?? 0
                    const coreTeamsCount = item.volunteer?.core_teams_count ?? 0
                    // Use filtered lead counts that exclude positions from events with include_in_duplicate_checks = false
                    const leadPositionsCount = (
                        (item.volunteer?.duplicate_check_event_leads_count ?? 0) +
                        (item.volunteer?.duplicate_check_areas_as_lead_count ?? 0) +
                        (item.volunteer?.duplicate_check_areas_as_deputy_lead_count ?? 0) +
                        (item.volunteer?.duplicate_check_shifts_as_lead_count ?? 0) +
                        (item.volunteer?.duplicate_check_shifts_as_deputy_lead_count ?? 0)
                    )
                    const totalCount = allocationsCount + coreTeamsCount + leadPositionsCount

                    return (
                        <div key={item.id} className="p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                            {/* Header with checkbox and name */}
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(item.id)}
                                        onChange={() => handleSelectOne(item.id)}
                                        className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                                    />
                                    <div>
                                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                                            {item.volunteer?.first_name} {item.volunteer?.last_name}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            {meta.idColumn}: {item.volunteer?.csid || 'N/A'}
                                        </div>
                                    </div>
                                </div>
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 font-semibold text-sm">
                                    {item.tier === 5 ? 'R' : item.tier}
                                </span>
                            </div>

                            {/* Streams and Role */}
                            <div className="mb-2 text-sm space-y-1">
                                <div className="text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">{meta.streamsColumn}:</span>{' '}
                                    <span className="inline-flex flex-wrap gap-1">
                                        {item.streams?.map((stream, idx) => (
                                            <span
                                                key={idx}
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                            >
                                                {stream}
                                            </span>
                                        ))}
                                    </span>
                                </div>
                                <div className="text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">{meta.roleColumn}:</span> {item.role}
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div className="grid grid-cols-1 gap-1 text-sm mb-2">
                                <div className="text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">{meta.phoneColumn}:</span> {item.volunteer?.phone || 'N/A'}
                                </div>
                                <div className="text-gray-600 dark:text-gray-400 break-all">
                                    <span className="font-medium">{meta.emailColumn}:</span> {item.volunteer?.email || 'N/A'}
                                </div>
                            </div>

                            {/* Status Badges */}
                            <div className="flex flex-wrap gap-2 mb-3">
                                <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        item.approved
                                            ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                            : 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'
                                    }`}
                                >
                                    {item.approved ? meta.approvedStatus : meta.pendingStatus}
                                </span>
                                <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        item.status === 'active'
                                            ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                    }`}
                                >
                                    {item.status === 'active' ? meta.activeStatus : meta.onHoldStatus}
                                </span>
                            </div>

                            {/* Duplications */}
                            {totalCount > 1 && (
                                <div className="mb-3">
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            setSelectedVolunteer(item)
                                            setShowAllocationsModal(true)
                                        }}
                                        className="w-full"
                                    >
                                        {meta.viewAllocationsButton(totalCount)}
                                    </Button>
                                </div>
                            )}

                            {/* Action Buttons */}
                            {(userHasApprovePermission || userHasManagePermission) && (
                                <div className="flex flex-wrap gap-2">
                                    {userHasApprovePermission && !item.approved && (
                                        <>
                                            <button
                                                onClick={() => onApprove(item.id)}
                                                className="flex-1 px-3 py-2 bg-success-600 text-white text-sm rounded hover:bg-success-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <CheckIcon className="w-4 h-4" />
                                                <span>{meta.approveButton}</span>
                                            </button>
                                            <button
                                                onClick={() => onReject(item.id)}
                                                className="flex-1 px-3 py-2 bg-danger-600 text-white text-sm rounded hover:bg-danger-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <XIcon className="w-4 h-4" />
                                                <span>{meta.rejectButton}</span>
                                            </button>
                                        </>
                                    )}
                                    {userHasApprovePermission && item.approved && (
                                        <button
                                            onClick={() => onUnapprove(item.id)}
                                            className="flex-1 px-3 py-2 bg-warn-600 text-white text-sm rounded hover:bg-warn-700 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <XIcon className="w-4 h-4" />
                                            <span>Unapprove</span>
                                        </button>
                                    )}
                                    {userHasManagePermission && (
                                        <>
                                            <button
                                                onClick={() => onEdit(item)}
                                                className="px-3 py-2 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <EditIcon className="w-4 h-4" />
                                                <span>Edit</span>
                                            </button>
                                            {item.status === 'active' ? (
                                                <button
                                                    onClick={() => onToggleStatus(item.id, 'on hold')}
                                                    className="px-3 py-2 bg-warn-600 text-white text-sm rounded hover:bg-warn-700 transition-colors flex items-center justify-center gap-2"
                                                >
                                                    <PauseIcon className="w-4 h-4" />
                                                    <span>Hold</span>
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => onToggleStatus(item.id, 'active')}
                                                    className="px-3 py-2 bg-success-600 text-white text-sm rounded hover:bg-success-700 transition-colors flex items-center justify-center gap-2"
                                                >
                                                    <PlayIcon className="w-4 h-4" />
                                                    <span>Active</span>
                                                </button>
                                            )}
                                            <button
                                                onClick={() => onDelete(item.id)}
                                                className="px-3 py-2 bg-danger-600 text-white text-sm rounded hover:bg-danger-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <DeleteIcon className="w-4 h-4" />
                                                <span>Delete</span>
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Notes (if present) */}
                            {item.notes && (
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                        {meta.notesColumn}
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        {item.notes}
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
                </div>
            </div>

            {/* No results message */}
            {paginatedData.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No core team members found matching your search.
                </div>
            )}

            {/* Pagination Controls */}
            {totalItems > 0 && (
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        {/* Rows per page selector */}
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-700 dark:text-gray-300">
                                {meta.rowsPerPageLabel || 'Rows per page'}:
                            </label>
                            <select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value))
                                    setCurrentPage(1)
                                }}
                                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>

                        {/* Page info */}
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            {meta.pageInfoLabel ? meta.pageInfoLabel(startIndex + 1, endIndex, totalItems) : `Showing ${startIndex + 1}-${endIndex} of ${totalItems}`}
                        </div>

                        {/* Page navigation */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {meta.previousPageLabel || 'Previous'}
                            </button>

                            {/* Page numbers */}
                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum
                                    if (totalPages <= 5) {
                                        pageNum = i + 1
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i
                                    } else {
                                        pageNum = currentPage - 2 + i
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`w-8 h-8 text-sm rounded transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-primary-600 text-white'
                                                    : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    )
                                })}
                            </div>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {meta.nextPageLabel || 'Next'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* Allocations Modal */}
        {showAllocationsModal && selectedVolunteer && (
            <Modal
                isOpen={showAllocationsModal}
                onClose={() => {
                    setShowAllocationsModal(false)
                    setSelectedVolunteer(null)
                }}
                title={`Duplications for ${selectedVolunteer.volunteer?.first_name} ${selectedVolunteer.volunteer?.last_name}`}
                maxWidth="5xl"
                footer={
                    <div className="flex justify-end">
                        <Button
                            onClick={() => {
                                setShowAllocationsModal(false)
                                setSelectedVolunteer(null)
                            }}
                            variant="outline"
                        >
                            Close
                        </Button>
                    </div>
                }
            >
                <div className="px-6 py-4">
                    {/* Shift Allocations */}
                    {selectedVolunteer.volunteer?.allocations && selectedVolunteer.volunteer.allocations.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                Shift Allocations ({selectedVolunteer.volunteer.allocations.length})
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Event</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Area</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duty</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date/Time</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {selectedVolunteer.volunteer.allocations.map((allocation: any, idx: number) => {
                                            const shift = allocation.shift
                                            const eventDate = shift?.area?.event?.start_date || shift?.area?.event?.date
                                            const startTime = shift?.start_time || 'N/A'
                                            const endTime = shift?.end_time || 'N/A'

                                            // Get status from allocation_times
                                            let statusText = 'Pending'
                                            let statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'

                                            if (allocation.allocation_times && allocation.allocation_times.length > 0) {
                                                const allocationTime = allocation.allocation_times[0]
                                                if (allocationTime.status === null) {
                                                    statusText = 'Pending'
                                                    statusClass = 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'
                                                } else if (allocationTime.status === true || allocationTime.status === 1) {
                                                    statusText = 'Accepted'
                                                    statusClass = 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                } else if (allocationTime.status === false || allocationTime.status === 0) {
                                                    statusText = 'Declined'
                                                    statusClass = 'bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200'
                                                }
                                            }

                                            return (
                                                <tr key={idx}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.area?.event?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.area?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {shift?.duty?.name || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {formatDateOnly(eventDate)} {startTime} - {endTime}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${statusClass}`}>
                                                            {statusText}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Core Team Assignments */}
                    {selectedVolunteer.volunteer?.coreTeams && selectedVolunteer.volunteer.coreTeams.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                Core Team Assignments ({selectedVolunteer.volunteer.coreTeams.length})
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Streams</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {selectedVolunteer.volunteer.coreTeams.map((coreTeam: any, idx: number) => {
                                            const statusText = coreTeam.approved ? 'Approved' : 'Submitted'
                                            const statusClass = coreTeam.approved
                                                ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                : 'bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200'

                                            return (
                                                <tr key={idx}>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {Array.isArray(coreTeam.streams) ? coreTeam.streams.join(', ') : coreTeam.streams || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                        {coreTeam.role || 'N/A'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${statusClass}`}>
                                                            {statusText}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Lead/Deputy Lead Positions */}
                    {(selectedVolunteer.volunteer?.eventLeads?.length > 0 ||
                      selectedVolunteer.volunteer?.areasAsLead?.length > 0 ||
                      selectedVolunteer.volunteer?.areasAsDeputyLead?.length > 0 ||
                      selectedVolunteer.volunteer?.shiftsAsLead?.length > 0 ||
                      selectedVolunteer.volunteer?.shiftsAsDeputyLead?.length > 0) && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                Lead/Deputy Lead Positions ({
                                    (selectedVolunteer.volunteer?.eventLeads?.length || 0) +
                                    (selectedVolunteer.volunteer?.areasAsLead?.length || 0) +
                                    (selectedVolunteer.volunteer?.areasAsDeputyLead?.length || 0) +
                                    (selectedVolunteer.volunteer?.shiftsAsLead?.length || 0) +
                                    (selectedVolunteer.volunteer?.shiftsAsDeputyLead?.length || 0)
                                })
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Event</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Area</th>
                                            <th className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duty</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {selectedVolunteer.volunteer?.eventLeads?.map((event: any, idx: number) => (
                                            <tr key={`event-lead-${idx}`}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Event</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{event.pivot?.role || 'Lead'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{event.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                            </tr>
                                        ))}
                                        {selectedVolunteer.volunteer?.areasAsLead?.map((area: any, idx: number) => (
                                            <tr key={`area-lead-${idx}`}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Area</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Lead</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.event?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                            </tr>
                                        ))}
                                        {selectedVolunteer.volunteer?.areasAsDeputyLead?.map((area: any, idx: number) => (
                                            <tr key={`area-deputy-${idx}`}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Area</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Deputy Lead</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.event?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{area.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">-</td>
                                            </tr>
                                        ))}
                                        {selectedVolunteer.volunteer?.shiftsAsLead?.map((shift: any, idx: number) => (
                                            <tr key={`shift-lead-${idx}`}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Shift</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Lead</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.event?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.duty?.name || 'N/A'}</td>
                                            </tr>
                                        ))}
                                        {selectedVolunteer.volunteer?.shiftsAsDeputyLead?.map((shift: any, idx: number) => (
                                            <tr key={`shift-deputy-${idx}`}>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Shift</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">Deputy Lead</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.event?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.area?.name || 'N/A'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{shift.duty?.name || 'N/A'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* No allocations message */}
                    {(!selectedVolunteer.volunteer?.allocations || selectedVolunteer.volunteer.allocations.length === 0) &&
                     (!selectedVolunteer.volunteer?.coreTeams || selectedVolunteer.volunteer.coreTeams.length === 0) &&
                     (!selectedVolunteer.volunteer?.eventLeads || selectedVolunteer.volunteer.eventLeads.length === 0) &&
                     (!selectedVolunteer.volunteer?.areasAsLead || selectedVolunteer.volunteer.areasAsLead.length === 0) &&
                     (!selectedVolunteer.volunteer?.areasAsDeputyLead || selectedVolunteer.volunteer.areasAsDeputyLead.length === 0) &&
                     (!selectedVolunteer.volunteer?.shiftsAsLead || selectedVolunteer.volunteer.shiftsAsLead.length === 0) &&
                     (!selectedVolunteer.volunteer?.shiftsAsDeputyLead || selectedVolunteer.volunteer.shiftsAsDeputyLead.length === 0) && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            No other allocations or core team assignments found.
                        </p>
                    )}
                </div>
            </Modal>
        )}
        </>
    )
}
